import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AlertTriangle, BookmarkCheck, CheckCircle2, ChevronLeft, ChevronRight, Flag, Send, Sparkles, XCircle } from 'lucide-react';
import { Button, ErrorState, LoadingState } from '../components/ui';
import { Html } from '../lib/htmlContent';
import { correctOptionKey, loadPublishedTest, optionsOf, scoreAttempt, submitExamAttempt, type QueRel } from '../lib/studentTestApi';
import { hasValidSession } from '../lib/parse';
import { Link } from 'react-router-dom';

type PaletteState = 'unanswered' | 'answered' | 'marked' | 'current';

export function StudentTestRunner() {
  const { testId = '' } = useParams();
  const [params] = useSearchParams();
  const examMode = params.get('mode') !== 'practice';
  const navigate = useNavigate();
  const [data, setData] = useState<Awaited<ReturnType<typeof loadPublishedTest>>>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<Map<number, number>>(new Map());
  const [celebratedIndex, setCelebratedIndex] = useState<number>();
  const [marked, setMarked] = useState<Set<number>>(new Set());
  const [remaining, setRemaining] = useState<number>(0);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [sessionState, setSessionState] = useState<'checking' | 'valid' | 'invalid'>(examMode ? 'checking' : 'valid');
  const submittedRef = useRef(false);

  useEffect(() => {
    if (!examMode) return;
    let active = true;
    void hasValidSession().then((ok) => { if (active) setSessionState(ok ? 'valid' : 'invalid'); });
    return () => { active = false; };
  }, [examMode]);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const value = await loadPublishedTest(testId);
        if (!active) return;
        if (!value || !value.questions.length) setError('No questions are available in this test.'); else {
          setData(value);
          setRemaining(Number(value.test.Time ?? 60) * 60);
        }
      } catch {
        if (active) setError('We could not load this test.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [testId]);

  const submit = useCallback(async () => {
    if (submittedRef.current || !data) return;
    submittedRef.current = true;
    setSubmitting(true);
    const score = scoreAttempt(data.questions, answers);
    let resultId: string | undefined;
    if (examMode) {
      try { resultId = await submitExamAttempt({ test: data.test, questions: data.questions, answers, score }); }
      catch { setSaveError(true); }
    }
    navigate(`/tests/result/${resultId ?? 'practice'}`, {
      state: { score, testTitle: data.test.TestTitle, courseTitle: data.test.CourseTitle, testInfoId: data.test.objectId, questions: data.questions, answers: [...answers.entries()], examMode },
    });
  }, [answers, data, examMode, navigate]);

  useEffect(() => {
    if (!data || loading || !examMode) return undefined;
    const timer = window.setInterval(() => setRemaining((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [data, loading, examMode]);

  useEffect(() => {
    if (examMode && data && remaining === 0 && !submittedRef.current) void submit();
  }, [remaining, data, submit, examMode]);

  const rels = data?.questions ?? [];
  const current: QueRel | undefined = rels[index];
  const question = current?.TestQuestionsPtr;
  const selected = answers.get(index);
  const correctKey = question ? correctOptionKey(question) : undefined;
  const clock = useMemo(() => {
    const h = Math.floor(remaining / 3600);
    const m = Math.floor((remaining % 3600) / 60);
    const s = remaining % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }, [remaining]);

  const paletteState = (position: number): PaletteState => {
    if (position === index) return 'current';
    if (marked.has(position)) return 'marked';
    if (answers.has(position)) return 'answered';
    return 'unanswered';
  };

  const choose = (key: number) => {
    if (!examMode && answers.has(index)) return;
    setAnswers((previous) => {
      const next = new Map(previous);
      if (examMode && next.get(index) === key) next.delete(index); else next.set(index, key);
      return next;
    });
    if (!examMode && key === correctKey) setCelebratedIndex(index);
    else setCelebratedIndex(undefined);
  };

  if (loading) return <LoadingState />;
  if (sessionState === 'invalid') return <div className="detail-page"><div className="player-error"><AlertTriangle size={26} /><p>Your session has expired, so this exam attempt cannot be recorded. Please sign in again to start a fresh attempt.</p><Link className="back-link" to="/login">Sign in again</Link></div></div>;
  if (error || !data || !question) return <div className="detail-page"><ErrorState onRetry={() => navigate(`/tests/${testId}`)} /></div>;

  const answeredCount = answers.size;

  return <div className="runner-page">
    <header className="runner-topbar">
      <div className="runner-title"><strong>{data.test.TestTitle}</strong><span>{examMode ? 'Exam mode' : 'Practice mode'} · {answeredCount}/{rels.length} answered</span></div>
      <div className="runner-topbar-actions">
        <button
          className="text-button"
          onClick={() => { if (window.confirm('Leave this test? Your answers will not be saved.')) navigate('/tests'); }}
        >Exit</button>
        {examMode && <span className={`timer-pill${remaining < 120 ? ' low' : ''}`}><AlertTriangle size={13} /> {clock}</span>}
      </div>
    </header>

    <article className="question-card">
      <div className="question-number">Q{index + 1} <span>of {rels.length} · {Number(question.Marks ?? 0)} mark{Number(question.Marks ?? 0) === 1 ? '' : 's'}</span>
        <button className={`mark-button${marked.has(index) ? ' active' : ''}`} onClick={() => setMarked((previous) => { const next = new Set(previous); if (next.has(index)) next.delete(index); else next.add(index); return next; })}>
          <Flag size={13} /> {marked.has(index) ? 'Marked' : 'Mark for review'}
        </button>
      </div>
      <Html html={question.QuesTitle} className="question-text" />
      <div className="answer-options">
        {optionsOf(question).map(({ key, html }) => (
          <button key={key} className={examMode ? (selected === key ? 'selected' : '') : selected === undefined ? '' : correctKey === undefined ? (selected === key ? 'selected' : '') : key === correctKey ? `right${selected === key ? ' selected-right' : ''}` : selected === key ? 'selected-wrong' : ''} disabled={!examMode && selected !== undefined} aria-pressed={selected === key} onClick={() => choose(key)}>
            <span>{String.fromCharCode(64 + key)}</span>
            <Html html={html} />
            {!examMode && selected !== undefined && correctKey !== undefined && key === correctKey && <CheckCircle2 size={16} />}
            {!examMode && selected === key && correctKey !== undefined && key !== correctKey && <XCircle size={16} />}
          </button>
        ))}
      </div>
      {!examMode && selected !== undefined && <div className="practice-review" role="status" aria-live="polite">
        {correctKey === undefined ? <strong className="practice-answer-unavailable">Answer key unavailable. This question could not be graded.</strong>
          : selected === correctKey
          ? <span className={`practice-celebration${celebratedIndex === index ? ' animate' : ''}`}><Sparkles size={17} /> Correct — nicely done!</span>
          : <strong>Not quite. The correct option is {String.fromCharCode(64 + correctKey)}.</strong>}
        {question.SolutionInfo ? <Html html={question.SolutionInfo} /> : null}
        {!hasPracticeExplanation(question.SolutionInfo) && <p>No explanation has been added for this question.</p>}
      </div>}
    </article>

    <div className="runner-actions">
      <Button variant="ghost" icon={ChevronLeft} disabled={index === 0} onClick={() => setIndex((value) => Math.max(0, value - 1))}>Previous</Button>
      {!examMode && selected !== undefined && correctKey !== undefined && (
        <span className={`instant-feedback${selected === correctKey ? ' right' : ' wrong'}`} aria-live="polite">
          {selected === correctKey ? 'Correct' : 'Incorrect'}
        </span>
      )}
      {index < rels.length - 1
        ? <Button icon={ChevronRight} onClick={() => setIndex((value) => Math.min(rels.length - 1, value + 1))}>Next</Button>
        : <Button icon={Send} onClick={() => setConfirming(true)}>{examMode ? 'Submit test' : 'Finish practice'}</Button>}
    </div>

    <section className="palette-panel">
      <h2><BookmarkCheck size={15} /> Question palette</h2>
      <div className="palette-grid">
        {rels.map((_rel, position) => <button key={position} className={`palette-cell ${paletteState(position)}`} onClick={() => setIndex(position)}>{position + 1}</button>)}
      </div>
      <div className="palette-legend">
        <span className="legend-answered">Answered</span><span className="legend-unanswered">Not answered</span><span className="legend-marked">Marked</span><span className="legend-current">Current</span>
      </div>
      {index === rels.length - 1 || confirming ? null : <Button variant="soft" onClick={() => setConfirming(true)}>{examMode ? 'Submit test' : 'Finish practice'}</Button>}
    </section>

    {confirming && (
      <div className="modal-backdrop" role="presentation">
        <div className="modal-card" role="dialog" aria-modal="true" aria-label="Submit confirmation">
          <h2>Submit this test?</h2>
          <p>You answered <strong>{answeredCount}</strong> of <strong>{rels.length}</strong> questions{rels.length - answeredCount > 0 ? ` — ${rels.length - answeredCount} will be scored as skipped` : ''}.{examMode ? ' This submits a scored result to your history.' : ' Practice mode does not save anything.'}</p>
          {saveError && <p className="save-warning">Your result could not be saved to your history — showing the local score instead.</p>}
          <div className="modal-actions">
            <Button variant="ghost" onClick={() => setConfirming(false)}>Keep working</Button>
            <Button disabled={submitting || (examMode && answeredCount === 0)} onClick={() => void submit()}>{submitting ? 'Submitting…' : examMode ? 'Submit now' : 'Finish practice'}</Button>
          </div>
        </div>
      </div>
    )}
  </div>;
}

function hasPracticeExplanation(value?: string) {
  const text = (value ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;|&#160;/gi, ' ')
    .replace(/✍️|🚩/g, ' ')
    .replace(/\bpoint\s*[-:]?\s*\d+(?:\.\d+)?\b/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text.replace(/\b(?:answer|correct|option)\b/gi, '').trim().length >= 30;
}
